import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { teamAPI } from '../../services/(adminapi)/teamAPI'

// Async thunks
export const fetchTeamMembers = createAsyncThunk(
  'team/fetchTeamMembers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await teamAPI.getTeamMembers()
      return response
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch team members')
    }
  }
)

export const fetchTeamInvitations = createAsyncThunk(
  'team/fetchTeamInvitations',
  async (_, { rejectWithValue }) => {
    try {
      const response = await teamAPI.getTeamInvitations()
      return response
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch team invitations')
    }
  }
)

export const deleteTeamMember = createAsyncThunk(
  'team/deleteTeamMember',
  async (memberId, { rejectWithValue }) => {
    try {
      const response = await teamAPI.deleteTeamMember(memberId)
      return { memberId, response }
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete team member')
    }
  }
)

export const inviteMember = createAsyncThunk(
  'team/inviteMember',
  async (data, { rejectWithValue }) => {
    try {
      const response = await teamAPI.inviteMember(data)
      return response
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to send invitation')
    }
  }
)

export const resendInvitation = createAsyncThunk(
  'team/resendInvitation',
  async (invitationId, { rejectWithValue }) => {
    try {
      const response = await teamAPI.resendInvitation(invitationId)
      return { invitationId, response }
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to resend invitation')
    }
  }
)


export const updateTeamMember = createAsyncThunk(
  'team/updateTeamMember',
  async ({ memberId, updateData }, { rejectWithValue }) => {
    try {
      const response = await teamAPI.updateTeamMember(memberId, updateData)
      return { memberId, response }
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update team member')
    }
  }
)

const initialState = {
  teamMembers: [],
  invitations: [],
  loading: false,
  error: null,
  success: null
}

const teamSlice = createSlice({
  name: 'team',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    clearSuccess: (state) => {
      state.success = null
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch team members
      .addCase(fetchTeamMembers.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchTeamMembers.fulfilled, (state, action) => {
        state.loading = false
        state.teamMembers = action.payload.teamMembers || []
        state.error = null
      })
      .addCase(fetchTeamMembers.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.teamMembers = []
      })
      
      // Fetch team invitations
      .addCase(fetchTeamInvitations.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchTeamInvitations.fulfilled, (state, action) => {
        state.loading = false
        state.invitations = action.payload.invitations || []
        state.error = null
      })
      .addCase(fetchTeamInvitations.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.invitations = []
      })
      
      // Delete team member
      .addCase(deleteTeamMember.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteTeamMember.fulfilled, (state, action) => {
        state.loading = false
        state.teamMembers = state.teamMembers.filter(
          member => member._id !== action.payload.memberId
        )
        state.success = 'Team member removed successfully'
      })
      .addCase(deleteTeamMember.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Invite member
      .addCase(inviteMember.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(inviteMember.fulfilled, (state) => {
        state.loading = false
        state.success = 'Invitation sent successfully'
      })
      .addCase(inviteMember.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Resend invitation
      .addCase(resendInvitation.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(resendInvitation.fulfilled, (state, action) => {
        state.loading = false
        state.success = 'Invitation resent successfully'
      })
      .addCase(resendInvitation.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
  
      .addCase(updateTeamMember.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateTeamMember.fulfilled, (state, action) => {
        state.loading = false
        state.success = 'Team member role updated successfully'
        // Update the specific member in the array
        const memberIndex = state.teamMembers.findIndex(member => member._id === action.payload.memberId)
        if (memberIndex !== -1) {
          state.teamMembers[memberIndex].role = action.payload.response.member?.role || action.payload.response.data?.role
        }
      })
      .addCase(updateTeamMember.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  }
})

export const { clearError, clearSuccess } = teamSlice.actions
export default teamSlice.reducer
